import React, { useRef } from 'react';
import Swal from 'sweetalert2';
import { MdCancel } from "react-icons/md";
import { DynamicTableService } from "../../../services/DynamicTableService";

const TableHeader = ({
                       stateGroupings,
                       columns,
                       calculateColumnWidths,
                       tooltipVisible,
                       setTooltipVisible,
                       headerRef,
                       startColumnResize,
                       effectiveSubdivisionWidths,
                       startSubdivisionResize,
                       cacheKey,
                       setCustomWidths,
                       setSubdivisionWidth,
                       setRowHeights,
                       useCustomWidths,
                       setUseCustomWidths,
                       listRef
                     }) => {
  const tooltipRefs = useRef({});
  const isMobile = window.innerWidth < 768;

  const handleResetCache = () => {
    if (!cacheKey) return;

    Swal.fire({
      title: 'Tem certeza?',
      text: 'Deseja apagar todas as alterações de tamanho das células e voltar para o padrão?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, resetar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await DynamicTableService.deleteTableDimensions(cacheKey);
        } catch (err) {
          console.error("Erro ao apagar dimensões do usuário:", err);
        }

        setCustomWidths({});
        setSubdivisionWidth({});

        setRowHeights(prev => {
          const newHeights = {};
          Object.keys(prev).forEach(key => {
            newHeights[key] = 40;
          });
          return newHeights;
        });

        setTimeout(() => {
          if (listRef?.current) {
            listRef.current.recomputeRowHeights();
            listRef.current.forceUpdateGrid();
          }
        }, 0);

        Swal.fire('Resetado!', 'As configurações foram restauradas.', 'success');
      }
    });
  };

  const calculateStateSpans = () => {
    if (!stateGroupings) return [];
    const spans = [];
    let currentState = '';
    let currentSpan = 0;

    stateGroupings.forEach((state, idx) => {
      if (state && state !== currentState) {
        if (currentSpan > 0) spans[spans.length - 1].span = currentSpan;
        spans.push({ state, startIndex: idx, span: 1 });
        currentState = state;
        currentSpan = 1;
      } else if (state === '' && currentState) currentSpan++;
      else spans.push({ state: '', startIndex: idx, span: 1 });
    });

    if (spans.length > 0 && currentSpan > 0) spans[spans.length - 1].span = currentSpan;
    return spans;
  };

  const renderStateGroupRow = () => {
    const stateSpans = calculateStateSpans();
    return (
      <div className="flex w-fit sticky top-0 z-30 bg-white">
        {stateSpans.map((spanInfo, spanIndex) => {
          let spanWidth = 0;
          for (let i = spanInfo.startIndex; i < spanInfo.startIndex + spanInfo.span; i++) {
            spanWidth += calculateColumnWidths[i] || 0;
          }

          return (
            <div
              key={`state-span-${spanIndex}`}
              className={`flex border bg-white px-4 py-1 font-bold text-center text-gray-800 sm:text-2xl text-xs items-center justify-center border-r-2 border-r-gray-300 ${
                spanInfo.startIndex === 0 ? 'static md:sticky left-0 z-20' : ''
              }`}
              style={{ width: spanWidth + 'px', minHeight: '40px' }}
            >
              {spanInfo.startIndex === 0 ? (
                <div className="flex flex-row items-center justify-between w-full">
                  <div className="flex items-center group">
                    <input
                      type="checkbox"
                      checked={useCustomWidths}
                      onChange={(e) => {
                        setUseCustomWidths(e.target.checked);

                        setTimeout(() => {
                          if (listRef?.current) {
                            listRef.current.recomputeRowHeights();
                            listRef.current.forceUpdateGrid();
                          }
                        }, 0);
                      }}
                      id="custom-widths-toggle"
                      className="peer hidden"
                    />

                    <label
                      htmlFor="custom-widths-toggle"
                      className="w-6 h-6 flex items-center justify-center rounded-full border border-gray-400 cursor-pointer
                      transition-colors peer-checked:bg-blue-600 peer-checked:border-blue-600"
                      title={useCustomWidths ? "Alternar para dimensões padrão" : "Alternar para dimensões personalizadas"}
                    >
                      <span className="text-white text-xs select-none">
                        ✔
                      </span>
                    </label>
                    <span
                      className="text-xs ml-2 md:hidden"
                    >
                      {useCustomWidths ? "Personalizado" : "Padrão"}
                    </span>
                  </div>

                  <MdCancel
                    onClick={handleResetCache}
                    className="text-3xl text-red-500 hover:text-red-700 transition-colors ease-in-out duration-150 cursor-pointer"
                    title="Resetar dimensões"
                  />
                </div>
              ) : (
                spanInfo.state
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCityRow = () => (
    <div className="flex w-fit relative">
      {columns.map((column, colIndex) => {
        const subWidths =
          colIndex === 0
            ? { city: calculateColumnWidths[colIndex], fao: 0 }
            : effectiveSubdivisionWidths[colIndex] || {
            city: calculateColumnWidths[colIndex] / 2,
            fao: calculateColumnWidths[colIndex] / 2
          };

        return (
          <div
            key={`column-${colIndex}`}
            className={`flex flex-row border h-10 bg-gray-50 font-bold border-r-2 border-gray-300 sm:text-base text-sm`}
            style={{
              width: calculateColumnWidths[colIndex] + 'px',
              minWidth: colIndex === 0 ? '280px' : '200px',
              position: colIndex === 0 ? (isMobile ? 'static' : 'sticky') : 'relative',
              left: colIndex === 0 ? 0 : 'auto',
              zIndex: colIndex === 0 ? 20 : 1,
              background: colIndex === 0 ? '#f9fafb' : 'inherit'
            }}
          >
            <div className="relative flex items-center justify-center border-r">
              <input
                type="text"
                value={column.split(' - ')[0]}
                readOnly
                title={column.split(' - ')[0]}
                className="bg-transparent border-none focus:outline-none text-ellipsis whitespace-nowrap overflow-hidden pl-4"
                style={{ width: subWidths.city }}
              />
            </div>

            {colIndex !== 0 && useCustomWidths && !isMobile && (
              <div
                className="absolute top-0 w-1 h-full cursor-col-resize hover:bg-blue-400 z-10"
                style={{ left: subWidths.city + 'px' }}
                onMouseDown={(e) =>
                  startSubdivisionResize(e, colIndex, 'city', column.split(' - ')[0])
                }
              />
            )}

            {colIndex !== 0 && (
              <div className="relative flex items-center">
                <p
                  className="cursor-pointer pl-4"
                  style={{ width: subWidths.fao }}
                  ref={(el) => (tooltipRefs.current[colIndex] = el)}
                  onMouseEnter={() => {
                    setTooltipVisible((prev) => {
                      const newArr = [...prev];
                      newArr[colIndex] = true;
                      return newArr;
                    });
                  }}
                  onMouseLeave={() => {
                    setTooltipVisible((prev) => {
                      const newArr = [...prev];
                      newArr[colIndex] = false;
                      return newArr;
                    });
                  }}
                >
                  (FAO)
                </p>

                {tooltipVisible[colIndex] && tooltipRefs.current[colIndex] && (
                  <div
                    className="absolute bg-white border rounded shadow-md p-2 text-sm text-gray-700 z-50"
                    style={{
                      position: 'fixed',
                      top: tooltipRefs.current[colIndex].getBoundingClientRect().bottom + 5,
                      left: tooltipRefs.current[colIndex].getBoundingClientRect().left
                    }}
                  >
                    <p>Fator de ajuste da rede operadora</p>
                  </div>
                )}
              </div>
            )}

            {useCustomWidths && !isMobile && (
              <div
                className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 z-20"
                onMouseDown={(e) => startColumnResize(e, colIndex)}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div ref={headerRef} className="sticky top-0 z-40 bg-white">
      {stateGroupings && renderStateGroupRow()}
      {renderCityRow()}
    </div>
  );
};

export default TableHeader;
